import { Key, ReactNode } from 'react';
import { Button, TmdxAvatar, Image } from '@ds';
import { ConditionalRender, TableColumnType } from '@ui';
import { getColorsByOrganType, getFullName, getOrganImg } from '@/utils/helpers';
import { SResource } from '@/api/apis/resources/schedule';

export const SURGEON_COLUMNS: TableColumnType[] = [
  {
    key: 'name',
    label: 'Name',
    isSortable: false,
    minWidthClassName: 'min-w-[230px]',
  },
  {
    key: 'organ',
    label: 'Organ',
    isSortable: false,
    minWidthClassName: 'min-w-[100px]',
  },
  {
    key: 'regionName',
    label: 'Region',
    isSortable: false,
    minWidthClassName: 'min-w-[180px]',
  },
  {
    key: 'insert',
    label: '',
    isSortable: false,
    minWidthClassName: 'min-w-11',
  },
];

type ResourceWithRegion = SResource & { regionName?: string; isSelected: boolean };

export const getRenderSurgeonTableCell =
  (currentRegionId: string, insertedIds: string[], onChange: (userId: string) => void) =>
  (resource: ResourceWithRegion, columnKey: Key): ReactNode => {
    const { id, isSelected, profilePhoto, firstName, lastName, organs, regionName, regionId } = resource;
    const fullName = getFullName(firstName, lastName);
    const isInserted = insertedIds.includes(id);
    const isHomeRegion = regionId === currentRegionId;
    const isDisabled = isInserted || isHomeRegion;

    switch (columnKey) {
      case 'name':
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-center min-w-[110px]">
              <TmdxAvatar radius="xl" size="xl" src={profilePhoto} name={fullName} />
            </div>
            <div className="flex flex-col">
              <span className="text-m-m">{fullName}</span>
              <span className="text-xs">Surgeon</span>
            </div>
          </div>
        );
      case 'organ':
        const organList = Array.from(new Set(organs));
        return (
          <div className="flex flex-row items-center gap-1">
            <ConditionalRender show={!!organList.length} placeholder="-">
              {organList.map((organ) => (
                <div
                  className={`${getColorsByOrganType(organ).bg50} w-6 h-6 rounded-full flex justify-center items-center`}
                  key={organ}
                >
                  <Image src={getOrganImg(organ)} alt={`${organ} image`} />
                </div>
              ))}
            </ConditionalRender>
          </div>
        );
      case 'regionName':
        return (
          <div className="flex items-center gap-2">
            <span className="w-36 text-m-m truncate">{regionName ?? '-'}</span>
          </div>
        );
      case 'insert':
        return (
          <Button
            size="sm"
            disableRipple
            isDisabled={isDisabled}
            variant={isSelected ? 'solid' : 'outline'}
            onClick={() => onChange(id)}
          >
            {isSelected ? 'Remove' : 'Assign'}
          </Button>
        );
      default:
        return null;
    }
  };
